import React, { useState, useEffect } from 'react';
import { getFeaturedTasks, searchCommunityTasksForAdmin, setFeaturedTasks } from '../services/api';
import './AdminFeaturedTasks.css';

const AdminFeaturedTasks = ({ standalone = true }) => {
  const [featuredTasks, setFeaturedTasksState] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('username'); // 'username' or 'title'

  // Load current featured tasks on component mount
  useEffect(() => {
    loadFeaturedTasks();
  }, []);

  const loadFeaturedTasks = async () => {
    try {
      setLoading(true);
      const response = await getFeaturedTasks();
      setFeaturedTasksState(response.featuredTasks || []);
      setSelectedTasks(response.featuredTasks?.map(task => task._id) || []);
    } catch (err) {
      console.error('Error loading featured tasks:', err);
      setError('Failed to load featured tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setError(null);
      
      const params = {};
      if (searchType === 'username') {
        params.username = searchQuery.trim();
      } else {
        params.taskTitle = searchQuery.trim();
      }

      const response = await searchCommunityTasksForAdmin(
        params.username, 
        params.taskTitle, 
        50
      );
      
      setSearchResults(response.tasks || []);
      
      if (response.tasks?.length === 0) {
        setError(response.message || 'No tasks found matching your search');
      }
    } catch (err) {
      console.error('Error searching tasks:', err);
      setError('Failed to search tasks');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleTaskSelection = (taskId) => {
    setSelectedTasks(prev => {
      if (prev.includes(taskId)) {
        // Remove task if already selected
        return prev.filter(id => id !== taskId);
      } else if (prev.length < 3) {
        // Add task if under limit
        return [...prev, taskId];
      } else {
        // Replace oldest selection if at limit
        return [...prev.slice(1), taskId];
      }
    });
  };

  const handleSaveFeaturedTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const response = await setFeaturedTasks(selectedTasks);
      
      setSuccess('Featured tasks updated successfully!');
      setFeaturedTasksState(response.featuredTasks || []);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error setting featured tasks:', err);
      setError(err.response?.data?.message || 'Failed to update featured tasks');
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedTasks([]);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={`admin-featured-tasks ${standalone ? 'standalone' : ''}`}>
      {standalone && <h1>Admin - Featured Tasks Management</h1>}
      
      {error && (
        <div className="admin-alert admin-alert-error">
          {error}
        </div>
      )}
      
      {success && (
        <div className="admin-alert admin-alert-success">
          {success}
        </div>
      )}

      {/* Current Featured Tasks Section */}
      <div className="admin-section">
        <h2>🌟 Current Featured Tasks ({featuredTasks.length}/3)</h2>
        {loading ? (
          <div className="admin-loading">Loading featured tasks...</div>
        ) : featuredTasks.length === 0 ? (
          <div className="admin-empty">No featured tasks currently set</div>
        ) : (
          <div className="admin-task-grid">
            {featuredTasks.map((task, index) => (
              <div key={task._id} className="admin-task-card featured">
                <div className="admin-task-header">
                  <span className="admin-task-position">#{index + 1}</span>
                  <h3>{task.title}</h3>
                  <span className="admin-task-type">{task.type}</span>
                </div>
                <div className="admin-task-meta">
                  <div>By: <strong>{task.createdBy?.username || 'Unknown'}</strong></div>
                  <div>Created: {formatDate(task.createdAt)}</div>
                  <div>Attempts: {task.attempts || 0} | Completions: {task.completions || 0}</div>
                </div>
                <div className="admin-task-description">
                  {task.description}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search and Selection Section */}
      <div className="admin-section">
        <h2>🔍 Search & Select Tasks</h2>
        
        <div className="admin-search-controls">
          <div className="admin-search-type">
            <label>
              <input
                type="radio"
                value="username"
                checked={searchType === 'username'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              Search by Username
            </label>
            <label>
              <input
                type="radio"
                value="title"
                checked={searchType === 'title'}
                onChange={(e) => setSearchType(e.target.value)}
              />
              Search by Task Title
            </label>
          </div>
          
          <div className="admin-search-input">
            <input
              type="text"
              placeholder={`Enter ${searchType === 'username' ? 'username' : 'task title'} to search...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button 
              onClick={handleSearch}
              disabled={searchLoading || !searchQuery.trim()}
              className="admin-search-btn"
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Selection Summary */}
        <div className="admin-selection-summary">
          <div className="admin-selected-count">
            Selected: {selectedTasks.length}/3 tasks
            {selectedTasks.length > 0 && (
              <button onClick={clearSelection} className="admin-clear-btn">
                Clear Selection
              </button>
            )}
          </div>
          
          {selectedTasks.length > 0 && (
            <button 
              onClick={handleSaveFeaturedTasks}
              disabled={loading}
              className="admin-save-btn"
            >
              {loading ? 'Saving...' : 'Save Featured Tasks'}
            </button>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="admin-search-results">
            <h3>Search Results ({searchResults.length})</h3>
            <div className="admin-task-grid">
              {searchResults.map((task) => (
                <div 
                  key={task._id} 
                  className={`admin-task-card ${selectedTasks.includes(task._id) ? 'selected' : ''}`}
                  onClick={() => handleTaskSelection(task._id)}
                >
                  <div className="admin-task-header">
                    <h4>{task.title}</h4>
                    <span className="admin-task-type">{task.type}</span>
                    {selectedTasks.includes(task._id) && (
                      <span className="admin-task-selected">✓ Selected</span>
                    )}
                  </div>
                  <div className="admin-task-meta">
                    <div>By: <strong>{task.createdBy?.username || 'Unknown'}</strong></div>
                    <div>Created: {formatDate(task.createdAt)}</div>
                    <div>👍 {task.upvotes || 0} | 👎 {task.downvotes || 0}</div>
                    <div>Attempts: {task.attempts || 0} | Completions: {task.completions || 0}</div>
                  </div>
                  <div className="admin-task-description">
                    {task.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminFeaturedTasks;